import { useRef, useState } from 'react';

interface UseSwiperTabScrollProps {
  itemLength: number;
  rootRef: React.RefObject<HTMLElement>;
}

const useSwiperTabScroll = ({ itemLength, rootRef }: UseSwiperTabScrollProps) => {
  /** 정확한 스크롤 위치를 보정하기 위한 고정 네비게이션 바의 높이를 참조하기 위한 ref */
  const fixedNavRef = useRef<HTMLDivElement>(null);
  /** 각 섹션의 ref를 저장하기 위한 배열 */
  const sectionArrRef = useRef<HTMLDivElement[]>(
    Array.from({ length: itemLength }, () => null as unknown as HTMLDivElement),
  );

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef(false);

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  /** 탭 클릭으로 인한 스크롤 위치 변경 시에는 IntersectionObserver의 onChange에 의해 유발되는 currentSectionIndex 변경을 막기 위한 플래그 */
  const setIsScrolling = (): void => {
    isScrollingRef.current = true;
    timerRef.current && clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => (isScrollingRef.current = false), 500);
  };

  /** 네비게이션 클릭 시 해당 섹션으로 스크롤 */
  const scrollToSection = (index: number, behavior: 'auto' | 'smooth' = 'smooth'): void => {
    const element = sectionArrRef.current[index];

    if (element) {
      const offsetHeight = fixedNavRef.current?.offsetHeight ?? 0;
      const offsetTop = Math.max(0, (element.offsetTop ?? 0) - offsetHeight + 1);

      setIsScrolling();
      rootRef.current?.scrollTo({ top: offsetTop, behavior });
    }
  };

  const updateCurrentSectionIndex = (index: number, isIntersecting: boolean): void => {
    if (index > currentSectionIndex) return; // index > currentSectionIndex 일때 => 최상단 섹션이 변경되었을 때만 업데이트 (아래에서 나타나는 섹션은 무시)
    if (isScrollingRef.current) return;

    const targetSectionIndex = isIntersecting ? index : index + 1; // // isIntersecting => (true: 스크롤을 올려서 해당 섹션이 나타남, false: 스크롤을 내려서 해당 섹션이 사라짐)

    setCurrentSectionIndex(targetSectionIndex);
  };

  return {
    sectionArrRef,
    fixedNavRef,
    currentSectionIndex,
    setCurrentSectionIndex,
    scrollToSection,
    updateCurrentSectionIndex,
  };
};

export default useSwiperTabScroll;
